import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useChat } from '../../../hooks/useChat';
import { getChatCompletion } from '../../../services/aiIntegrations/chatCompletion';
import { X, ChevronDown, Send, Sparkles, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import FIELD_SCHEMAS from './fieldSchemas';

// ── Build system prompt for conversational mode ──────────────────────────────
const buildConversationalSystemPrompt = (purchaseType, schema) => {
  const label = schema?.label || purchaseType;
  const fieldList = schema?.fields?.map(f => `- ${f?.label} (${f?.hint})`)?.join('\n') || '';
  const requiredLabels = schema?.required?.map(id => schema?.fields?.find(f => f?.id === id)?.label)?.filter(Boolean)?.join(', ') || '';

  // Subcategory-specific opening question hints so the AI starts naturally
  const openingHints = {
    'watches': `Start by asking what occasion or lifestyle they're shopping for — for example: "Are you looking for something to wear every day, a dress watch for special occasions, or more of a sport/fitness watch?"`,
    'sunglasses': `Start by asking what they'll primarily use them for — for example: "Are these for everyday wear, outdoor activities like hiking or driving, or more of a fashion statement?"`,
    'belts': `Start by asking what they need the belt for — for example: "Are you looking for something to wear with dress pants, jeans, or more of a casual everyday belt?"`,
    'bags': `Start by asking what they'll use the bag for — for example: "Are you looking for something for work, travel, everyday errands, or a special occasion?"`,
    'wallets': `Start by asking how they carry their wallet — for example: "Do you prefer something slim that fits in a front pocket, a classic bifold, or do you need something with more room for cards and cash?"`,
    'jewelry-rings': `Start by asking what the ring is for — for example: "Is this for everyday wear, a special occasion, or are you looking for something like a statement cocktail ring?"`,
    'jewelry-earrings': `Start by asking about their style — for example: "Are you looking for something understated like studs or small hoops, or more of a statement pair for a special occasion?"`,
    'jewelry-bracelets': `Start by asking what the bracelet is for — for example: "Is this something you'd wear every day, or more of a special occasion piece?"`,
    'jewelry-necklaces': `Start by asking about the occasion — for example: "Are you looking for a delicate everyday necklace, something layered, or a statement piece for a special event?"`,
    'jewelry-pendants': `Start by asking if there's a specific meaning or theme — for example: "Are you looking for something personal like an initial or birthstone, or more of a classic design like a cross or heart?"`,
    'jewelry-anklets': `Start by asking about their style preference — for example: "Are you looking for something dainty and minimal, or do you prefer something with charms or a bit more detail?"`,
    'crawl-space': `Start by asking what's prompting the project — for example: "Are you dealing with moisture or water issues down there, or is this more about adding insulation, a vapor barrier, or getting a general inspection done?"`,
  };

  const openingHint = openingHints[purchaseType] || `Start by asking the single most important question to understand what they're looking for in a ${label}.`;

  return `You are a friendly shopping consultant helping someone find the perfect ${label}.

Your job is to have a natural, conversational chat to help them figure out exactly what they need. Don't use forms or lists — just talk like a knowledgeable friend who knows a lot about ${label}s.

${openingHint}

As you chat, try to naturally gather information about these areas:
${fieldList}

The most important things to find out are: ${requiredLabels}.

Guidelines:
- Ask one or two questions at a time — don't overwhelm them
- Be warm, friendly, and encouraging
- If they give a vague answer, gently ask for more detail
- Share helpful context when relevant (e.g., "Most people looking for a dress watch go with...")
- Keep responses concise — 2-4 sentences max
- Once you've gathered the required info plus a few optional details, you can wrap up naturally
- Never mention JSON, fields, forms, or technical terms`;
};

// ── Build extraction prompt for silent JSON pass ─────────────────────────────
const buildExtractionPrompt = (purchaseType, schema, conversationHistory) => {
  const fieldDefs = schema?.fields?.map(f =>
    `  "${f?.id}": ""  // ${f?.label} — e.g. ${f?.hint}`
  )?.join(',\n') || '';

  const fieldMappingGuide = schema?.fields?.map(f =>
    `- "${f?.id}" maps to: ${f?.label} (example values: ${f?.hint})`
  )?.join('\n') || '';

  const conversationText = conversationHistory
    ?.filter(m => m?.role !== 'system')
    ?.map(m => `${m?.role === 'user' ? 'USER' : 'ASSISTANT'}: ${m?.content}`)
    ?.join('\n\n');

  return `You are a data extraction assistant. Your only job is to read a conversation and output a JSON object.

PURCHASE TYPE: ${schema?.label || purchaseType}

FIELD MAPPING GUIDE — use these to find values in the conversation:
${fieldMappingGuide}

CONVERSATION TO ANALYZE:
${conversationText}

RULES:
1. Read every message in the conversation carefully
2. For each field ID in the JSON template, find the matching value using the field mapping guide
3. Copy the user's exact words/numbers/dates as the value
4. If a field was not discussed at all, leave it as an empty string ""
5. CRITICAL: Return ONLY the raw JSON object — no markdown fences, no code blocks, no explanation text before or after

JSON TEMPLATE (replace empty strings with extracted values):
{
${fieldDefs}
}`;
};

// ── Typewriter hook: animates text character by character ─────────────────────
function useTypewriter(targetText, speed = 18) {
  const [displayed, setDisplayed] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const animFrameRef = useRef(null);
  const indexRef = useRef(0);
  const targetRef = useRef(targetText);

  useEffect(() => {
    targetRef.current = targetText;

    if (!targetText) {
      setDisplayed('');
      indexRef.current = 0;
      setIsTyping(false);
      return;
    }

    // If new text is longer than what we've typed, keep animating forward
    if (targetText.length > indexRef.current) {
      setIsTyping(true);

      const tick = () => {
        const current = indexRef.current;
        const target = targetRef.current;

        if (current < target.length) {
          // Advance by a few chars per frame for smoother feel at higher speeds
          const charsPerTick = Math.max(1, Math.floor(speed / 10));
          const nextIndex = Math.min(current + charsPerTick, target.length);
          indexRef.current = nextIndex;
          setDisplayed(target.slice(0, nextIndex));
          animFrameRef.current = setTimeout(tick, speed);
        } else {
          setIsTyping(false);
        }
      };

      if (animFrameRef.current) clearTimeout(animFrameRef.current);
      animFrameRef.current = setTimeout(tick, speed);
    }

    return () => {
      if (animFrameRef.current) clearTimeout(animFrameRef.current);
    };
  }, [targetText, speed]);

  // Reset when targetText resets to empty
  useEffect(() => {
    if (!targetText) {
      indexRef.current = 0;
      setDisplayed('');
      setIsTyping(false);
    }
  }, [targetText]);

  return { displayed, isTyping };
}

// ── Loading dots animation ────────────────────────────────────────────────────
const TypingIndicator = () => (
  <div className="flex items-center gap-1 px-3 py-2.5 bg-white rounded-2xl rounded-bl-sm shadow-sm border border-gray-100 w-fit">
    <span className="text-xs text-gray-400 mr-1">Thinking</span>
    {[0, 1, 2].map(i => (
      <span
        key={i}
        className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
        style={{ animationDelay: `${i * 0.15}s`, animationDuration: '0.8s' }}
      />
    ))}
  </div>
);

// ── Streaming message bubble with typewriter effect ───────────────────────────
const StreamingMessage = ({ content, isActive }) => {
  const { displayed, isTyping } = useTypewriter(content || '', 12);

  const renderContent = (text) => {
    if (!text) return null;
    const stripped = text.replace(/\[\d+\]/g, '');
    const parts = stripped?.split(/(\*\*[^*]+\*\*)/g);
    return parts?.map((part, i) => {
      if (part?.startsWith('**') && part?.endsWith('**')) {
        return <strong key={i}>{part?.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  return (
    <span>
      {renderContent(displayed)}
      {(isTyping || (isActive && displayed.length < (content || '').length)) && (
        <span className="inline-block w-1.5 h-3.5 bg-gray-400 ml-0.5 animate-pulse rounded-sm align-middle" />
      )}
    </span>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const NeedsAIAssistant = ({ purchaseType, formData, handleInputChange, onPopulateFields, autoOpen, onPopulateComplete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [conversationHistory, setConversationHistory] = useState([]);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isPopulated, setIsPopulated] = useState(false);
  // Track which message index is currently streaming
  const [streamingIndex, setStreamingIndex] = useState(null);
  // Track if we're waiting for the very first response (intro loading state)
  const [isWaitingForIntro, setIsWaitingForIntro] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const conversationHistoryRef = useRef([]);
  const hasRestoredConversation = useRef(false);

  const schema = FIELD_SCHEMAS?.[purchaseType];

  const { response, isLoading, error, sendMessage } = useChat(
    'PERPLEXITY',
    'perplexity/sonar-pro',
    true
  );

  const streamingMessageRef = useRef(null);

  useEffect(() => {
    if (error) toast?.error('Purchase assistant error. Please try again.');
  }, [error]);

  // Restore saved conversation from formData on mount
  useEffect(() => {
    if (hasRestoredConversation.current) return;
    const saved = formData?.assistantConversation;
    if (saved?.messages?.length > 0 && saved?.conversationHistory?.length > 0) {
      hasRestoredConversation.current = true;
      setMessages(saved.messages);
      setConversationHistory(saved.conversationHistory);
      conversationHistoryRef.current = saved.conversationHistory;
    }
  }, [formData?.assistantConversation]);

  const hasAutoOpened = useRef(false);
  useEffect(() => {
    if (autoOpen && schema && !hasAutoOpened.current) {
      hasAutoOpened.current = true;
      handleOpen();
    }
  }, [autoOpen, schema]);

  // Handle streaming: update the streaming message content in real time
  useEffect(() => {
    if (!isLoading && response && streamingMessageRef?.current !== null) {
      // Streaming done — finalize the message with full content
      setMessages(prev => {
        const updated = [...prev];
        updated[streamingMessageRef.current] = {
          role: 'assistant',
          content: response,
          finalized: true,
        };
        return updated;
      });
      setStreamingIndex(null);
      setIsWaitingForIntro(false);
      // Add to conversation history
      setConversationHistory(prev => {
        const withoutLastAssistant = prev?.filter((_, i) => !(i === prev?.length - 1 && prev?.[prev?.length - 1]?.role === 'assistant'));
        const updated = [...withoutLastAssistant, { role: 'assistant', content: response }];
        conversationHistoryRef.current = updated;
        return updated;
      });
      streamingMessageRef.current = null;
    } else if (isLoading && response) {
      // Still streaming — update content in place for typewriter to consume
      setIsWaitingForIntro(false);
      setMessages(prev => {
        if (streamingMessageRef?.current === null) {
          const newIndex = prev?.length;
          streamingMessageRef.current = newIndex;
          setStreamingIndex(newIndex);
          return [...prev, { role: 'assistant', content: response, finalized: false }];
        }
        const updated = [...prev];
        updated[streamingMessageRef.current] = {
          role: 'assistant',
          content: response,
          finalized: false,
        };
        return updated;
      });
    }
  }, [response, isLoading]);

  useEffect(() => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Open chat and send initial greeting
  const handleOpen = () => {
    setIsOpen(true);
    setIsPopulated(false);
    const hasExistingConversation = conversationHistoryRef.current?.length > 0 || messages?.length > 0;
    if (!hasExistingConversation) {
      const systemPrompt = buildConversationalSystemPrompt(purchaseType, schema);
      const label = schema?.label || purchaseType;
      const initHistory = [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: `Hi, I'm looking to buy a ${label} and could use some help figuring out what I need.` },
      ];
      setConversationHistory(initHistory);
      conversationHistoryRef.current = initHistory;
      setIsWaitingForIntro(true);
      sendMessage(initHistory, { temperature: 0.8, max_tokens: 800 });
    }
    setTimeout(() => inputRef?.current?.focus(), 300);
  };

  const handleClose = () => setIsOpen(false);

  // Helper to render markdown bold (**text**) as <strong> tags, and strip citation brackets like [1][2]
  const renderMessageContent = (content) => {
    if (!content) return null;
    // Remove citation brackets like [1], [2][4][5], etc.
    const stripped = content.replace(/\[\d+\]/g, '');
    const parts = stripped?.split(/(\*\*[^*]+\*\*)/g);
    return parts?.map((part, i) => {
      if (part?.startsWith('**') && part?.endsWith('**')) {
        return <strong key={i}>{part?.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  const handleSend = () => {
    const text = inputValue?.trim();
    if (!text || isLoading) return;

    const userMessage = { role: 'user', content: text };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');

    const updatedHistory = [...conversationHistoryRef.current, userMessage];
    setConversationHistory(updatedHistory);
    conversationHistoryRef.current = updatedHistory;
    streamingMessageRef.current = null;

    sendMessage(updatedHistory, { temperature: 0.8, max_tokens: 800 });
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSend();
    }
  };

  const handlePopulateNeeds = async () => {
    if (!schema) return;
    setIsExtracting(true);

    try {
      // Use the ref for always-current history; patch in the latest streaming response if needed
      let fullHistory = [...conversationHistoryRef.current];
      const latestAssistantContent = response && response.trim() ? response : null;
      if (
        latestAssistantContent &&
        (fullHistory.length === 0 ||
          fullHistory[fullHistory.length - 1]?.role !== 'assistant' ||
          fullHistory[fullHistory.length - 1]?.content !== latestAssistantContent)
      ) {
        fullHistory = [...fullHistory, { role: 'assistant', content: latestAssistantContent }];
      }

      const extractionPrompt = buildExtractionPrompt(purchaseType, schema, fullHistory);

      const result = await getChatCompletion(
        'PERPLEXITY',
        'perplexity/sonar-pro',
        [
          {
            role: 'system',
            content:
              'You are a JSON extraction assistant. Extract values from the conversation and return ONLY a valid JSON object matching the template. No markdown fences, no explanation, no extra text — just the raw JSON object.',
          },
          { role: 'user', content: extractionPrompt },
        ],
        {
          temperature: 0.0,
          max_tokens: 1000,
        }
      );

      if (result?.error) {
        throw new Error(result.error);
      }
      const rawContent = result?.choices?.[0]?.message?.content || '';

      let extracted = null;
      const cleaned = rawContent
        ?.replace(/```json\s*/gi, '')
        ?.replace(/```\s*/g, '')
        ?.replace(/<think>[\s\S]*?<\/think>/gi, '')
        ?.trim();

      try {
        extracted = JSON.parse(cleaned);
      } catch {
        // Fallback: find the first complete JSON object in the response
        const jsonMatch = cleaned?.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          try {
            extracted = JSON.parse(jsonMatch[0]);
          } catch {
            extracted = null;
          }
        }
      }

      if (!extracted) {
        throw new Error('Could not parse JSON from extraction response');
      }

      // Populate form fields — exact match first, then case-insensitive fallback
      const extractedLower = Object.fromEntries(
        Object.entries(extracted).map(([k, v]) => [k.toLowerCase().trim(), v])
      );

      let populatedCount = 0;
      const batchFields = {};
      schema?.fields?.forEach(field => {
        let value = extracted?.[field?.id];
        if (value === undefined || value === null || value === '') {
          value = extractedLower?.[field?.id?.toLowerCase()];
        }
        if (value && value !== '' && value !== 'null' && value !== 'N/A' && value !== 'n/a') {
          batchFields[field?.id] = String(value);
          populatedCount++;
        }
      });

      if (populatedCount > 0) {
        if (onPopulateFields) {
          onPopulateFields(batchFields);
        } else {
          // Fallback: call handleInputChange individually (may only set last field)
          Object.entries(batchFields).forEach(([id, val]) => handleInputChange(id, val));
        }
      }

      setIsExtracting(false);
      setIsPopulated(true);

      if (populatedCount > 0) {
        toast?.success(`${populatedCount} field${populatedCount > 1 ? 's' : ''} populated!`);
      } else {
        toast('No values could be extracted. Try sharing more details in the chat.');
        setIsPopulated(false);
      }

      if (populatedCount > 0) {
        // Save conversation to formData before closing
        const currentMessages = messages;
        const currentHistory = conversationHistoryRef.current;
        if (onPopulateFields) {
          // Also save conversation snapshot via a special key
          onPopulateFields({ __conversationSnapshot: JSON.stringify({ messages: currentMessages, conversationHistory: currentHistory }) });
        }
        setTimeout(() => {
          setIsOpen(false);
          if (onPopulateComplete) onPopulateComplete();
        }, 2000);
      }
    } catch (err) {
      setIsExtracting(false);
      toast?.error('Could not extract values. Please try again.');
    }
  };

  if (!schema) return null;

  return (
    <div className="relative">
      {/* Toggle Button */}
      {!isOpen && (
        <button
          onClick={handleOpen}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#F06F6B] to-[#F58A54] text-white text-sm font-medium rounded-full shadow-md hover:shadow-lg hover:from-[#e05e5a] hover:to-[#e07843] transition-all duration-200"
        >
          <Sparkles size={15} />
          Purchase Assistant
        </button>
      )}

      {/* Chat Panel */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
          <div className="pointer-events-auto w-full max-w-sm bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden" style={{ height: '520px' }}>
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#F06F6B] to-[#F58A54]">
              <div className="flex items-center gap-2">
                <Sparkles size={16} className="text-white" />
                <span className="text-white font-semibold text-sm">Purchase Assistant</span>
                <span className="text-white/70 text-xs">· {schema?.label}</span>
              </div>
              <button onClick={handleClose} className="text-white/70 hover:text-white transition-colors">
                <X size={18} />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-gray-50">
              {/* Intro loading state — shown before first message arrives */}
              {isWaitingForIntro && messages?.length === 0 && (
                <div className="flex flex-col items-center justify-center h-full gap-4">
                  <div className="flex flex-col items-center gap-3">
                    <div className="relative">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#F06F6B] to-[#F58A54] flex items-center justify-center shadow-md">
                        <Sparkles size={22} className="text-white" />
                      </div>
                      <span className="absolute -bottom-1 -right-1 w-4 h-4 bg-white rounded-full flex items-center justify-center shadow-sm">
                        <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-700">Your assistant is getting ready…</p>
                    <TypingIndicator />
                  </div>
                </div>
              )}

              {/* Empty state (no intro loading, no messages) */}
              {!isWaitingForIntro && messages?.length === 0 && (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <Sparkles size={24} className="text-[#F06F6B] mx-auto mb-2" />
                    <p className="text-sm text-gray-500">Starting conversation...</p>
                  </div>
                </div>
              )}

              {messages?.map((msg, idx) => (
                <div key={idx} className={`flex ${msg?.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                      msg?.role === 'user' ?'bg-gradient-to-r from-[#F06F6B] to-[#F58A54] text-white rounded-br-sm' :'bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-sm'
                    }`}
                  >
                    {msg?.role === 'assistant' && idx === streamingIndex ? (
                      <StreamingMessage
                        content={msg?.content}
                        isActive={isLoading && idx === streamingIndex}
                      />
                    ) : (
                      <>
                        {renderMessageContent(msg?.content)}
                        {msg?.role === 'assistant' && isLoading && idx === messages?.length - 1 && idx !== streamingIndex && (
                          <span className="inline-block w-1.5 h-3.5 bg-gray-400 ml-1 animate-pulse rounded-sm" />
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}

              {/* Typing indicator between messages (not intro) */}
              {isLoading && !isWaitingForIntro && messages?.length > 0 && streamingIndex === null && (
                <div className="flex justify-start">
                  <TypingIndicator />
                </div>
              )}

              {/* Populate button */}
              {conversationHistory?.filter(m => m?.role === 'user' && !m?.content?.startsWith('Hi, I\'m looking to buy'))?.length >= 1 && !isPopulated && (
                <div className="flex justify-center pt-2">
                  <button
                    onClick={handlePopulateNeeds}
                    disabled={isExtracting || isLoading}
                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-sm font-semibold rounded-xl shadow-md hover:shadow-lg hover:from-emerald-600 hover:to-teal-600 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isExtracting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Populating...
                      </>
                    ) : (
                      <>
                        <ChevronDown size={16} />
                        Populate My Needs →
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Success state */}
              {isPopulated && (
                <div className="flex justify-center pt-2">
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-medium rounded-xl">
                    <CheckCircle size={16} />
                    Fields populated! Closing...
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="px-3 py-3 bg-white border-t border-gray-100">
              <div className="flex items-center gap-2 bg-gray-50 rounded-xl border border-gray-200 px-3 py-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e?.target?.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type your answer..."
                  disabled={isLoading || isPopulated}
                  className="flex-1 bg-transparent text-sm text-gray-800 placeholder-gray-400 outline-none disabled:opacity-50"
                />
                <button
                  onClick={handleSend}
                  disabled={!inputValue?.trim() || isLoading || isPopulated}
                  className="p-1.5 bg-gradient-to-r from-[#F06F6B] to-[#F58A54] text-white rounded-lg hover:from-[#e05e5a] hover:to-[#e07843] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Send size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NeedsAIAssistant;
